import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { ReportSort } from '@/store/slices/reportingV2/reportingV2.api';
import {
  type WidgetState,
  fetchReportWidget,
  selectReportingV2Filters,
  selectReportingV2Widget,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { useEffect } from 'react';
import { buildRequest } from '../widgets';

interface WidgetQuery {
  dimensions: string[];
  metrics: string[];
  sort?: ReportSort[];
  limit?: number;
  /** Row offset for paged grids. Part of the signature so paging refetches. */
  offset?: number;
  /** Skip the fetch (e.g. a tab whose data isn't modelled yet). Defaults to true. */
  enabled?: boolean;
  /** Forces the request to one or more DSPs regardless of the global Platform filter — see `WidgetSpec.dspsOverride`. */
  dspsOverride?: string[];
}

/**
 * Owns a single Reporting 2.0 widget's fetch so components stay render-only
 * (per "data fetching lives in hooks, never in components"). The query is
 * serialized into the effect's dep, so callers can pass an inline object without
 * the dispatch→render→new-object→dispatch loop that bit several widgets — the
 * fetch refires only when the dimensions/metrics/sort/limit or the active
 * filters actually change.
 */
export const useReportWidget = (key: string, query: WidgetQuery): WidgetState => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectReportingV2Filters);
  const widget = useAppSelector(selectReportingV2Widget(key));

  const { dimensions, metrics, sort, limit, offset, enabled = true, dspsOverride } = query;
  // `signature` is the sole effect trigger: it captures every input the request
  // derives from, so an inline `query` object can't refire the effect on each
  // render. The fetch runs only when these values actually change.
  const signature = JSON.stringify({ dimensions, metrics, sort, limit, offset, enabled, dspsOverride, filters });

  useEffect(() => {
    if (!enabled) return;
    dispatch(
      fetchReportWidget({
        key,
        request: buildRequest({ key, title: '', dimensions, metrics, sort, limit, offset, dspsOverride }, filters),
      }),
    );
  }, [dispatch, key, signature]);

  return widget;
};
